// @givmo/assistant-panel — tolerant decode for the two wire families, hand-modelled because neither
// is in the committed OpenAPI snapshot (see `assistant-transport.ts`).
//
// Every decoder here is TOTAL: it answers a usable value for a malformed, partial or future payload
// and never throws. That is the same posture `@givmo/ai`'s block decoder takes and for the same
// reason — a panel that crashes on an unfamiliar field takes the whole surface down, while one that
// renders what it understood keeps working. These types and functions go away when a routine
// snapshot refresh absorbs both families; the tolerance moves into each query's `select`.

/** A message as the conversation read and the create response project it. */
export interface AssistantMessageRow {
  id: string;
  /** Ordinal within the conversation. `0` is the server-generated opening message. */
  seq: number;
  /** `user` or `assistant`. */
  role: string;
  /** The concatenated text blocks — the render-ready projection. */
  text: string;
  status: string;
  /** The additive `ui_blocks` contract field, raw. Decoded by `@givmo/ai`. */
  uiBlocks: readonly unknown[];
}

/** One conversation, as the list read and the detail read project it. */
export interface AssistantConversationRow {
  id: string;
  /** The creation provenance. Read for `surface` when matching this surface's persistent thread. */
  entryContext: Record<string, unknown> | null;
  /** Present on the detail + create reads; empty from the list read, which carries no messages. */
  messages: readonly AssistantMessageRow[];
}

/** The `202` a queued send answers. */
export interface SendAcceptedRow {
  turnId: string;
  /** The path to tail for the reply. Relative to the API origin. */
  streamPath: string;
}

/** The organization's consent instrument plus its standing. */
export interface OrgConsentRow {
  /** The organization has an acceptance on file (at ANY version). */
  accepted: boolean;
  /** EXACTLY the turn gate's predicate. False ⇒ an organization-audience turn is refused. */
  currentVersionAccepted: boolean;
  /** The latest event is a revocation — "switched off", which reads differently from "never on". */
  revoked: boolean;
  /** The instrument body to render. The canonical bytes, never re-wrapped. */
  content: string;
  /** The sha256 of `content`, echoed back on accept so a stale sheet cannot bind. */
  contentHash: string;
  versionNumber: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

function asInteger(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

/** One message row. A row with no id is dropped by the caller — it cannot be keyed or acted on. */
export function decodeMessage(raw: unknown): AssistantMessageRow | null {
  const row = asRecord(raw);
  if (row === null) return null;
  const id = asString(row.id);
  if (id.length === 0) return null;
  return {
    id,
    seq: asInteger(row.seq),
    role: asString(row.role),
    text: asString(row.text),
    status: asString(row.status),
    uiBlocks: asArray(row.ui_blocks),
  };
}

/** A conversation row from either read. `messages` is absent on the list read and empty here. */
export function decodeConversation(raw: unknown): AssistantConversationRow | null {
  const row = asRecord(raw);
  if (row === null) return null;
  const id = asString(row.id);
  if (id.length === 0) return null;
  const messages: AssistantMessageRow[] = [];
  for (const entry of asArray(row.messages)) {
    const message = decodeMessage(entry);
    if (message !== null) messages.push(message);
  }
  messages.sort((a, b) => a.seq - b.seq);
  return { id, entryContext: asRecord(row.entry_context), messages };
}

/**
 * The list read's rows.
 *
 * ⚠ The middleware has already unwrapped `{data, meta, links}` to the bare array, so this receives a
 * list. It also tolerates the unwrapped-envelope shape, because a caller that skipped the middleware
 * (a test double, a future direct fetch) would otherwise silently decode to nothing.
 */
export function decodeConversations(raw: unknown): readonly AssistantConversationRow[] {
  const list = Array.isArray(raw) ? raw : asArray(asRecord(raw)?.data);
  const rows: AssistantConversationRow[] = [];
  for (const entry of list) {
    const row = decodeConversation(entry);
    if (row !== null) rows.push(row);
  }
  return rows;
}

/**
 * This surface's PERSISTENT thread — the newest conversation created from it (D-EP-40: one thread,
 * not one per visit).
 *
 * The list arrives newest-activity-first, so the first match is the one a person is actually using.
 * Matching on `entry_context.surface` is what keeps a portal thread and a consumer thread apart in
 * one person's history; a conversation created before this panel existed carries no surface and is
 * correctly not adopted.
 */
export function findSurfaceThread(
  conversations: readonly AssistantConversationRow[],
  wireSurface: string,
  match?: Readonly<Record<string, string>>,
): AssistantConversationRow | null {
  return (
    conversations.find(
      (conversation) =>
        asString(conversation.entryContext?.surface) === wireSurface &&
        Object.entries(match ?? {}).every(
          ([key, value]) => asString(conversation.entryContext?.[key]) === value,
        ),
    ) ?? null
  );
}

/** The `202` body. A response missing `stream_path` yields `null` — there is nothing to tail. */
export function decodeSendAccepted(raw: unknown): SendAcceptedRow | null {
  const row = asRecord(raw);
  if (row === null) return null;
  const streamPath = asString(row.stream_path);
  if (streamPath.length === 0) return null;
  return { turnId: asString(row.turn_id), streamPath };
}

/**
 * The consent state, from either the read or the `428`'s `context.org_consent`.
 *
 * ⚠ `null` for anything that is not a state object — including the ABSENT case, where the gate omits
 * `org_consent` entirely because the environment serves no instrument. Absent and unparseable land
 * in the same place on purpose: in both, there is no sheet to render.
 */
export function decodeOrgConsent(raw: unknown): OrgConsentRow | null {
  const row = asRecord(raw);
  if (row === null) return null;
  const content = asString(row.content);
  const contentHash = asString(row.current_content_hash);
  if (content.length === 0 || contentHash.length === 0) return null;
  return {
    accepted: asBoolean(row.accepted),
    currentVersionAccepted: asBoolean(row.current_version_accepted),
    revoked: asBoolean(row.revoked),
    content,
    contentHash,
    versionNumber: asInteger(row.current_version_number),
  };
}
